import { isLayoutField } from '@core-engine/schema/constants.js'
import { flattenFields } from '@core-engine/schema/field-helpers/index.js'
import type {
  FieldType,
  Schema,
  SchemaField,
  SchemaFilter,
  SchemaFilterOption
} from '@core-engine/schema/types.js'
import { createAuthorshipFields } from '@next-adapter/generators/actions/action-helpers.js'

export interface EntityFieldContext {
  dbFields: SchemaField[]
  regularDbFields: SchemaField[]
  allDbFields: SchemaField[]
}

export type ResolvedEntityFilterType = 'select' | 'boolean' | 'date-range'
export type ResolvedEntityFilterSource = 'builtin' | 'distinct' | 'static'
export type BuiltInEntityFilterKey = 'status' | 'createdAt'

export interface ResolvedEntityFilter {
  key: string
  field: string
  label: string
  type: ResolvedEntityFilterType
  source: ResolvedEntityFilterSource
  options: SchemaFilterOption[]
  fieldType: FieldType
  builtIn?: BuiltInEntityFilterKey
}

const FILTERABLE_FIELD_TYPES = new Set<FieldType>([
  'string',
  'email',
  'varchar',
  'text',
  'boolean',
  'number',
  'decimal',
  'date',
  'timestamp',
  'time',
  'select'
])

const BUILT_IN_FILTER_KEYS = new Set<BuiltInEntityFilterKey>(['status', 'createdAt'])

function createAutoField(name: string, type: FieldType): SchemaField {
  return {
    name,
    type,
    required: true
  }
}

function getSchemaFieldByName(fields: SchemaField[], name: string): SchemaField | undefined {
  return fields.find((field) => field.name === name)
}

function ensureFilterableField(
  field: SchemaField | undefined,
  filter: SchemaFilter,
  schemaName: string
): SchemaField {
  if (!field) {
    throw new Error(
      `Schema "${schemaName}" defines a filter for "${filter.field}", but that field does not exist.`
    )
  }

  if (!FILTERABLE_FIELD_TYPES.has(field.type)) {
    throw new Error(
      `Schema "${schemaName}" defines a filter for "${filter.field}", but "${field.type}" fields are not filterable.`
    )
  }

  return field
}

export function getEntityFieldContext(schema: Schema): EntityFieldContext {
  const dbFields = flattenFields(schema.fields).filter((field) => !isLayoutField(field.type))
  const regularDbFields = dbFields.filter(
    (field) => !(field.type === 'relationship' && field.multiple === true)
  )

  const allDbFields = [...regularDbFields]

  if (schema.actions?.draft === true && !getSchemaFieldByName(allDbFields, 'published')) {
    allDbFields.push(createAutoField('published', 'boolean'))
  }

  if (!getSchemaFieldByName(allDbFields, 'createdAt')) {
    allDbFields.push(createAutoField('createdAt', 'timestamp'))
  }

  if (!getSchemaFieldByName(allDbFields, 'updatedAt')) {
    allDbFields.push(createAutoField('updatedAt', 'timestamp'))
  }

  for (const field of createAuthorshipFields()) {
    if (!getSchemaFieldByName(allDbFields, field.name)) {
      allDbFields.push(field)
    }
  }

  if (!getSchemaFieldByName(allDbFields, 'sortOrder')) {
    allDbFields.push(createAutoField('sortOrder', 'number'))
  }

  return {
    dbFields,
    regularDbFields,
    allDbFields
  }
}

export function getFilterableFields(fields: SchemaField[]): SchemaField[] {
  return fields.filter(
    (field) =>
      !field.primaryKey && field.name !== 'sortOrder' && FILTERABLE_FIELD_TYPES.has(field.type)
  )
}

export function resolveEntityFilters(schema: Schema): ResolvedEntityFilter[] {
  const { allDbFields } = getEntityFieldContext(schema)
  const resolved: ResolvedEntityFilter[] = []
  const seenKeys = new Set<string>()
  const reservedFields = new Set<string>()

  const publishedField = getSchemaFieldByName(allDbFields, 'published')
  if (publishedField?.type === 'boolean') {
    resolved.push({
      key: 'status',
      field: 'published',
      label: 'Status',
      type: 'select',
      source: 'builtin',
      options: [
        { label: 'Published', value: 'published' },
        { label: 'Draft', value: 'draft' }
      ],
      fieldType: publishedField.type,
      builtIn: 'status'
    })
    seenKeys.add('status')
    reservedFields.add('published')
  }

  const createdAtField = getSchemaFieldByName(allDbFields, 'createdAt')
  if (createdAtField) {
    resolved.push({
      key: 'createdAt',
      field: 'createdAt',
      label: 'Created',
      type: 'date-range',
      source: 'builtin',
      options: [],
      fieldType: createdAtField.type,
      builtIn: 'createdAt'
    })
    seenKeys.add('createdAt')
    reservedFields.add('createdAt')
  }

  for (const filter of schema.filters || []) {
    const key = filter.key || filter.field

    if (BUILT_IN_FILTER_KEYS.has(key as BuiltInEntityFilterKey)) {
      throw new Error(
        `Schema "${schema.name}" defines a filter with reserved key "${key}". Choose a different filter key.`
      )
    }

    if (seenKeys.has(key)) {
      throw new Error(
        `Schema "${schema.name}" defines duplicate filter key "${key}". Filter keys must be unique.`
      )
    }

    if (reservedFields.has(filter.field)) {
      throw new Error(
        `Schema "${schema.name}" defines a filter for "${filter.field}", but that field already has a built-in filter.`
      )
    }

    const field = ensureFilterableField(
      getSchemaFieldByName(allDbFields, filter.field),
      filter,
      schema.name
    )

    const type = filter.type ?? 'select'
    if (type === 'boolean' && field.type !== 'boolean') {
      throw new Error(
        `Schema "${schema.name}" defines boolean filter "${key}", but "${filter.field}" is not a boolean field.`
      )
    }

    const source =
      type === 'boolean'
        ? 'static'
        : (filter.source ?? (filter.options && filter.options.length > 0 ? 'static' : 'distinct'))

    const options =
      type === 'boolean'
        ? [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ]
        : source === 'static'
          ? (filter.options ?? [])
          : []

    if (type === 'select' && source === 'static' && options.length === 0) {
      throw new Error(`Schema "${schema.name}" defines static filter "${key}" without any options.`)
    }

    resolved.push({
      key,
      field: filter.field,
      label: filter.label,
      type,
      source,
      options,
      fieldType: field.type
    })
    seenKeys.add(key)
  }

  return resolved
}
